"use client";

import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ChevronRight, ChevronLeft, Send, Loader, CircleCheck } from "lucide-react";
const formConfig = {
  formId: "puppy_application_form_home",
  sectionName: "Puppy Application",
  defaultValues: {
    full_name: "",
    email_address: "",
    phone_number: "",
    home_type: "",
    yard_size: "",
    dog_experience: "",
    hours_at_home: "",
    reason_for_puppy: "",
    reference_contact: "",
    home_photos: ""
  },
  validationRules: {
    full_name: {
      required: true
    },
    email_address: {
      required: true,
      pattern: "email"
    },
    phone_number: {
      required: true,
      pattern: "phone"
    },
    home_type: {
      required: true
    },
    dog_experience: {
      required: true
    },
    hours_at_home: {
      required: true,
      min: 0,
      max: 24
    },
    reason_for_puppy: {
      required: true,
      minLength: 30,
      maxLength: 500
    }
  }
};
const homeTypeOptions = ["Single-family house with yard", "Townhouse", "Apartment (no pets allowed)", "Apartment (pet friendly)", "Other"];
const yardSizeOptions = ["No yard", "Small (≤ 500 sq ft)", "Medium (501-1500 sq ft)", "Large (> 1500 sq ft)"];
const experienceOptions = ["First time dog owner", "Previously owned dogs (no longer)", "Currently own dogs"];
function ApplicationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const nextStep = () => setStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));
  const progressValue = step / totalSteps * 100;
  const inputStyles = "border-0 border-b border-secondary/30 rounded-none px-0 focus-visible:ring-0 focus-visible:border-secondary bg-transparent shadow-none text-primary-foreground placeholder:text-primary-foreground/40 md:text-lg h-12 transition-colors";
  const labelStyles = "text-secondary/80 font-sans text-sm uppercase tracking-wider mb-2";
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-10 text-secondary" />
        </div>
        <h3 className="font-serif text-3xl md:text-4xl text-secondary">Application Received</h3>
        <p className="text-primary-foreground/80 max-w-md mx-auto font-sans leading-relaxed">
          {successMessage || "Thank you for your interest. We will review your application and contact you shortly regarding the next steps in our adoption process."}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <div className="space-y-4 mb-12">
        <div className="flex justify-between text-xs font-sans uppercase tracking-widest text-secondary/70">
          <span>Step {step} of {totalSteps}</span>
          <span>{step === 1 ? 'Personal' : step === 2 ? 'Lifestyle' : 'Preferences'}</span>
        </div>
        <Progress value={progressValue} className="h-1 bg-primary-foreground/10 [&>div]:bg-secondary" />
      </div>

      {}
      <div className="min-h-[400px]">
        {}
        <div className={`space-y-8 transition-all duration-500 ${step === 1 ? 'opacity-100 translate-x-0' : 'hidden opacity-0 translate-x-8'}`}>
          <h3 className="font-serif text-2xl text-secondary mb-8">Personal Details</h3>
          
          <FormField control={control} name="full_name" rules={{
          required: "Full name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className={labelStyles}>Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="Jane Doe" className={inputStyles} {...field} />
                </FormControl>
                <FormDescription className="text-primary-foreground/50 text-xs">First and last name as it appears on legal documents.</FormDescription>
                <FormMessage className="text-red-400" />
              </FormItem>} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField control={control} name="email_address" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelStyles}>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jane@example.com" className={inputStyles} {...field} />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>} />

            <FormField control={control} name="phone_number" rules={{
            required: "Phone number is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelStyles}>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(555) 123-4567" className={inputStyles} {...field} />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>} />
          </div>
        </div>

        {}
        <div className={`space-y-8 transition-all duration-500 ${step === 2 ? 'opacity-100 translate-x-0' : 'hidden opacity-0 translate-x-8'}`}>
          <h3 className="font-serif text-2xl text-secondary mb-8">Lifestyle & Home</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField control={control} name="home_type" rules={{
            required: "Please select your home type"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelStyles}>Type of Home</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className={inputStyles}>
                        <SelectValue placeholder="Select your home type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-primary text-primary-foreground border-secondary/30">
                      {homeTypeOptions.map((option, i) => <SelectItem key={i} value={option} data-index={i} className="focus:bg-secondary/20 focus:text-secondary cursor-pointer">
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-red-400" />
                </FormItem>} />

            <FormField control={control} name="yard_size" render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelStyles}>Yard Size (if applicable)</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className={inputStyles}>
                        <SelectValue placeholder="Select yard size" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-primary text-primary-foreground border-secondary/30">
                      {yardSizeOptions.map((option, i) => <SelectItem key={i} value={option} data-index={i} className="focus:bg-secondary/20 focus:text-secondary cursor-pointer">
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-red-400" />
                </FormItem>} />
          </div>

          <FormField control={control} name="hours_at_home" rules={{
          required: "This field is required",
          min: {
            value: 0,
            message: "Minimum 0"
          },
          max: {
            value: 24,
            message: "Maximum 24"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className={labelStyles}>Typical Hours at Home per Day</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="8" className={inputStyles} {...field} />
                </FormControl>
                <FormDescription className="text-primary-foreground/50 text-xs">Average number of hours you are present in the house.</FormDescription>
                <FormMessage className="text-red-400" />
              </FormItem>} />

          <FormField control={control} name="home_photos" render={({
          field
        }) => <FormItem>
                <FormLabel className={labelStyles}>Upload Photos of Your Home (optional)</FormLabel>
                <FormControl>
                  <Input type="file" accept="image/jpeg, image/png" className={`${inputStyles} file:text-secondary file:font-sans file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:bg-secondary/10 hover:file:bg-secondary/20 cursor-pointer`} {...field} />
                </FormControl>
                <FormDescription className="text-primary-foreground/50 text-xs">Show us the area where the puppy will live. Accepted formats: JPG, PNG. Max 5MB.</FormDescription>
                <FormMessage className="text-red-400" />
              </FormItem>} />
        </div>

        {}
        <div className={`space-y-8 transition-all duration-500 ${step === 3 ? 'opacity-100 translate-x-0' : 'hidden opacity-0 translate-x-8'}`}>
          <h3 className="font-serif text-2xl text-secondary mb-8">Experience & Preferences</h3>

          <FormField control={control} name="dog_experience" rules={{
          required: "Please select your experience level"
        }} render={({
          field
        }) => <FormItem className="space-y-4">
                <FormLabel className={labelStyles}>Experience with Dogs</FormLabel>
                <FormControl>
                  <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex flex-col space-y-3">
                    {experienceOptions.map((option, i) => <FormItem key={i} data-index={i} className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value={option} className="border-secondary text-secondary focus-visible:ring-secondary" />
                        </FormControl>
                        <FormLabel className="font-sans text-primary-foreground font-normal cursor-pointer">
                          {option}
                        </FormLabel>
                      </FormItem>)}
                  </RadioGroup>
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>} />

          <FormField control={control} name="reason_for_puppy" rules={{
          required: "Please provide a reason",
          minLength: {
            value: 30,
            message: "Please provide a bit more detail (min 30 characters)"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className={labelStyles}>Why Do You Want a Puppy?</FormLabel>
                <FormControl>
                  <Textarea placeholder="Explain your motivation and how a puppy fits into your lifestyle..." className={`${inputStyles} min-h-[120px] resize-none py-4`} {...field} />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>} />

          <FormField control={control} name="reference_contact" render={({
          field
        }) => <FormItem>
                <FormLabel className={labelStyles}>Reference (Name & Phone)</FormLabel>
                <FormControl>
                  <Input placeholder="John Smith – (555) 987-6543" className={inputStyles} {...field} />
                </FormControl>
                <FormDescription className="text-primary-foreground/50 text-xs">A trusted individual who can speak to your suitability as a dog owner.</FormDescription>
                <FormMessage className="text-red-400" />
              </FormItem>} />
        </div>
      </div>

      {}
      {submitError && <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm font-sans text-center">
          {submitError}
        </div>}

      {}
      <div className="flex items-center justify-between pt-8 border-t border-secondary/20 mt-12">
        <Button type="button" variant="ghost" onClick={prevStep} disabled={step === 1 || isSubmitting} className={`text-primary-foreground hover:text-secondary hover:bg-transparent ${step === 1 ? 'invisible' : ''}`}>
          <ChevronLeft className="mr-2 size-4" />
          Back
        </Button>

        {step < totalSteps ? <Button type="button" onClick={nextStep} className="border-secondary text-secondary hover:bg-secondary hover:text-primary bg-transparent border-2 rounded-full px-8 py-6 font-serif tracking-wider uppercase text-sm transition-all">
            Next Step
            <ChevronRight className="ml-2 size-4" />
          </Button> : <Button type="submit" disabled={isSubmitting} className="border-secondary text-secondary hover:bg-secondary hover:text-primary bg-transparent border-2 rounded-full px-8 py-6 font-serif tracking-wider uppercase text-sm transition-all">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Submitting...
              </> : <>
                Submit Application
                <Send className="ml-2 size-4" />
              </>}
          </Button>}
      </div>
    </Form>;
}
export default function PuppyApplicationForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="puppyapplicationform" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="text-center mb-12 md:mb-16 space-y-4">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-primary tracking-tight">Puppy Ownership Application EXAMPLE</h2>
            <p className="font-sans text-muted-foreground max-w-2xl mx-auto text-lg">
              Begin your journey to welcoming a new family member. Please provide detailed information to help us ensure the perfect match.
            </p>
          </div>

          <div className="bg-primary rounded-[2rem] p-8 md:p-12 lg:p-16 shadow-2xl relative overflow-hidden">
            {}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-secondary to-transparent opacity-50" />
            <div className="absolute -top-24 -right-24 size-64 bg-secondary/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 size-64 bg-secondary/5 rounded-full blur-3xl pointer-events-none" />
            
            <div className="relative z-10">
              <FormProvider formId={formConfig.formId} sectionName={formConfig.sectionName} defaultValues={formConfig.defaultValues} validationRules={formConfig.validationRules} submitText="Submit Application" successMessage="Your application has been successfully submitted. We will be in touch soon!">
                <ApplicationFormContent />
              </FormProvider>
            </div>
          </div>
        </div>

      </div>
    </section>;
}